/**
 * ORTLIEB: UTM Tracking via Cart Metafields
 *
 * Captures UTM parameters from the URL and stores them in a cross-subdomain
 * cookie. On the first add-to-cart event the UTM values are written to cart
 * metafields via the Shopify Storefront API (cartMetafieldsSet mutation) so
 * they can be copied to order metafields at checkout.
 *
 * Cookie: ortlieb_utm – JSON-encoded object, valid 7 days, set on the
 *         root domain so all localised subdomains share the same value.
 *
 * Cart metafields set (namespace "custom", type "single_line_text_field"):
 *   custom.utm_source, custom.utm_medium, custom.utm_campaign,
 *   custom.utm_term, custom.utm_content
 *
 * Prerequisites:
 *   - Cart metafield definitions must exist in the Shopify admin
 *     (Settings → Custom data → Cart) for each utm key with namespace "custom".
 *   - If the metafield values should carry over to orders, enable the
 *     "Cart to order copyable" capability on matching order metafield definitions.
 */

const UTM_COOKIE_NAME = "ortlieb_utm";
const UTM_COOKIE_DAYS = 7;
const UTM_SYNCED_FLAG = "ortlieb_utm_synced";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const STOREFRONT_API_VERSION = "2026-01";

/* ------------------------------------------------------------------ */
/*  Cookie helpers                                                     */
/* ------------------------------------------------------------------ */

/**
 * Derive the root domain from the current hostname so the cookie is
 * shared across all subdomains (e.g. ".ortlieb.com").
 *
 * Falls back to no explicit domain (current hostname only) when running
 * on *.myshopify.com or other public-suffix domains where browsers
 * refuse cross-subdomain cookies.
 */
function getRootDomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  // Public suffixes where setting a parent-domain cookie is blocked
  const publicSuffixes = ["myshopify.com", "shopify.com", "trycloudflare.com"];
  if (parts.length >= 2) {
    const parentDomain = parts.slice(-2).join(".");
    if (publicSuffixes.includes(parentDomain)) {
      return null; // fall back to current hostname only
    }
    return "." + parentDomain;
  }

  return null;
}

function setCookie(name, value, days, domain) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  const domainPart = domain ? `;domain=${domain}` : "";
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax${domainPart}`;
}

function getCookie(name) {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(";");
  for (const raw of cookies) {
    const cookie = raw.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/*  1. Capture UTM parameters from the URL into a cookie               */
/* ------------------------------------------------------------------ */

function captureUtmParams() {
  const params = new URLSearchParams(window.location.search);
  const utmValues = {};

  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) {
      utmValues[key] = value;
    }
  }

  if (Object.keys(utmValues).length === 0) return;

  setCookie(UTM_COOKIE_NAME, JSON.stringify(utmValues), UTM_COOKIE_DAYS, getRootDomain());
  // Reset the synced flag so the new UTM values are pushed on the next add-to-cart
  sessionStorage.removeItem(UTM_SYNCED_FLAG);
}

/* ------------------------------------------------------------------ */
/*  2. Push UTM values to cart metafields on first add-to-cart          */
/* ------------------------------------------------------------------ */

function getStoredUtm() {
  const raw = getCookie(UTM_COOKIE_NAME);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Retrieve the cart token from the AJAX Cart API (/cart.js).
 * The token is used to construct the Storefront API cart GID.
 */
async function getCartToken() {
  const response = await fetch(`${Shopify.routes.root}cart.js`);
  if (!response.ok) return null;
  const cart = await response.json();
  return cart.token || null;
}

/**
 * Call the Storefront API cartMetafieldsSet mutation (tokenless access).
 */
async function setCartMetafields(cartToken, utmValues) {
  const cartGid = `gid://shopify/Cart/${cartToken}`;

  const metafields = Object.entries(utmValues).map(([key, value]) => ({
    ownerId: cartGid,
    key: `custom.${key}`,
    type: "single_line_text_field",
    value: value,
  }));

  const query = `
    mutation cartMetafieldsSet($metafields: [CartMetafieldsSetInput!]!) {
      cartMetafieldsSet(metafields: $metafields) {
        metafields {
          key
          value
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables: { metafields } }),
  });

  if (!response.ok) {
    throw new Error(`Storefront API responded with ${response.status}`);
  }

  const result = await response.json();
  const userErrors = result?.data?.cartMetafieldsSet?.userErrors;

  if (userErrors && userErrors.length > 0) {
    throw new Error(`cartMetafieldsSet errors: ${JSON.stringify(userErrors)}`);
  }

  return result;
}

async function syncUtmToCart() {
  // Only sync once per session
  if (sessionStorage.getItem(UTM_SYNCED_FLAG)) return;

  const utmValues = getStoredUtm();
  if (!utmValues || Object.keys(utmValues).length === 0) return;

  try {
    const cartToken = await getCartToken();
    if (!cartToken) return;

    await setCartMetafields(cartToken, utmValues);
    sessionStorage.setItem(UTM_SYNCED_FLAG, "1");
  } catch (e) {
    console.warn("ORTLIEB UTM tracking: failed to sync UTM to cart metafields", e);
  }
}

/* ------------------------------------------------------------------ */
/*  Initialise                                                         */
/* ------------------------------------------------------------------ */

// Step 1: always capture UTM params if present in the current URL
captureUtmParams();

// Step 2: listen for add-to-cart and sync UTM values once
document.addEventListener("variant:add", () => syncUtmToCart());
